import { cx } from '@emotion/css';
import { ButtonBase, Typography } from '@mui/material';
import {
  Node,
  NodeProps,
  ReactFlowState,
  useStore,
  useViewport,
} from '@xyflow/react';
import { makeStyles } from 'tss-react/mui';

import { COLORS } from '@/styles/tokens/colors';

export interface PhaseBandProps extends Record<string, unknown> {
  label: string;
  /**
   * Optional. When set, the label is a button that calls it, so the user can
   * edit the label. Without it, the band is not interactive.
   */
  onEdit?: () => void;
}

export type PhaseBandNodeProps = NodeProps<Node<PhaseBandProps>>;

const canvasWidthSelector = ({ width }: ReactFlowState) => width;

// Clear space between the label and the left edge of the visible canvas. It is
// the same margin the estate waterfall keeps for its section labels.
const LABEL_LEFT_MARGIN = 16;

const useStyles = makeStyles()(() => ({
  root: {
    // The node's own box, which the band builder sizes as the tiles the band
    // divides plus a left gutter for this label.
    position: 'absolute',
    inset: 0,
    // A decoration only — never a drag/click target so it can't steal clicks
    // from the tiles and edges it sits behind.
    pointerEvents: 'none',
  },
  rule: {
    // Laid out against the visible canvas rather than the node's box, so the
    // divider spans the page whatever the content underneath it measures.
    // `left` and `width` come from the viewport transform below.
    position: 'absolute',
    top: '50%',
    borderTop: `1px dashed ${COLORS.GRAY[300]}`,
  },
  chip: {
    // Laid out against the visible canvas, like the rule. `left` comes from the
    // viewport transform below.
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    padding: '2px 10px',
    borderRadius: 4,
    // Opaque background so the dashed rule doesn't strike through the label.
    backgroundColor: COLORS.GRAY[50],
  },
  editableChip: {
    // The band root ignores the pointer. Only an editable label receives it.
    pointerEvents: 'all',
    '&:hover': { backgroundColor: COLORS.GRAY[100] },
  },
  label: {
    color: COLORS.GRAY[500],
    whiteSpace: 'nowrap',
  },
}));

/**
 * A horizontal phase separator for presentation diagrams. It shows a dashed rule
 * across the page, and a small label that names the transition (for example "At
 * death"). It goes behind the tiles and the edges, by the negative node zIndex
 * the adapter sets. It is not interactive, except for a label that a diagram
 * makes editable with `onEdit`.
 *
 * The rule and the label are both placed against the visible canvas, not against
 * the node box. A band divides the page, so the rule must touch both edges of the
 * page, and the label must stay at the left edge of the page — the same behavior
 * as the section labels of the estate waterfall. They must keep those positions
 * when the user moves a tile, changes the window size, pans, or zooms. A node that
 * is as wide as the page cannot do this. Such a node needs a new width for each
 * change to the viewport, and the editor must then keep those changes out of its
 * undo history.
 *
 * The band builder still reserves a gutter at the left of the node box. The gutter
 * is not where the label sits. It is the clear space that keeps the label off the
 * leftmost cell when the fit puts the content against the left edge of the page,
 * because the band sits behind the diagram and the opaque edge-label chips paint
 * over it.
 */
export function PhaseBandNode({
  data: { label, onEdit },
  width,
  positionAbsoluteX,
}: PhaseBandNodeProps) {
  const { classes } = useStyles();
  const canvasWidth = useStore(canvasWidthSelector);
  const { x: viewportX, zoom } = useViewport();

  // Change the visible canvas into the flow units of this node, then subtract the
  // node origin to get a local `left`.
  //
  // The canvas width is 0 until the pane is measured, and a print or a thumbnail
  // can render before that. Then use the node box, so that the rule and the label
  // are always visible. They stay with the tiles for that frame.
  const hasMeasuredCanvas = canvasWidth > 0;
  const visibleWidth = hasMeasuredCanvas ? canvasWidth / zoom : (width ?? 0);
  const canvasLeft = hasMeasuredCanvas
    ? -viewportX / zoom - positionAbsoluteX
    : 0;
  // The margin is in flow units, so the label keeps the same distance from the
  // page edge as the gutter keeps from the leftmost cell, at every zoom.
  const labelLeft = hasMeasuredCanvas ? canvasLeft + LABEL_LEFT_MARGIN : 0;
  const text = (
    <Typography variant="body-xs" className={classes.label}>
      {label}
    </Typography>
  );

  return (
    <div className={classes.root}>
      <div
        className={classes.rule}
        style={{ left: canvasLeft, width: visibleWidth }}
      />
      <div
        // `nodrag` and `nopan` stop React Flow from using a click on an
        // editable label to drag or pan.
        className={cx(
          classes.chip,
          onEdit && [classes.editableChip, 'nodrag', 'nopan']
        )}
        style={{ left: labelLeft }}
      >
        {onEdit ? <ButtonBase onClick={onEdit}>{text}</ButtonBase> : text}
      </div>
    </div>
  );
}
